import { useState } from 'react';
import { Maja } from './Mascot.jsx';
import { ChunkyButton } from './Buttons.jsx';
import CroFlag from './CroFlag.jsx';
import { setSourceLang, completeOnboarding } from '../store/progress.js';
import { BALL } from '../theme.js';

const T = BALL;

// Default language picked from the browser's locale: Swedish for sv-*,
// English for everything else. The user can change this in step one of
// onboarding, and again later in Settings.
function defaultLang() {
  if (typeof navigator === 'undefined') return 'sv';
  const l = (navigator.language || 'sv').toLowerCase();
  return l.startsWith('sv') ? 'sv' : 'en';
}

function LangPick({ value, current, label, sub, onSelect }) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      style={{
        appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
        flex: 1, padding: '14px 14px',
        background: active ? '#FFFFFF' : 'rgba(255,255,255,0.08)',
        color: active ? T.primaryDeep : '#FFFFFF',
        border: `2px solid ${active ? '#FFFFFF' : 'rgba(255,255,255,0.4)'}`,
        borderRadius: 14,
        boxShadow: active ? `0 4px 0 rgba(0,0,0,0.15)` : 'none',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
      }}
    >
      <span style={{ fontSize: 17, fontWeight: 900 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.8 }}>{sub}</span>
    </button>
  );
}

export default function Onboarding({ onDone }) {
  // Use Swedish for the welcome copy by default — the user has to actively
  // choose English. Most users only learn from one language at a time so
  // a single switch up front is enough.
  const [lang, setLang] = useState(defaultLang());

  function start() {
    setSourceLang(lang);
    completeOnboarding();
    onDone?.();
  }

  const copy = lang === 'en' ? {
    hello: 'Bok!',
    welcome: "Welcome to Ballingo. Let's get you speaking Croatian.",
    pickLang: 'Show instructions in',
    sv: 'Swedish',
    svSub: 'Hej!',
    en: 'English',
    enSub: 'Hello!',
    note: "You can change this any time in Settings.",
    cta: "Let's go",
  } : {
    hello: 'Bok!',
    welcome: 'Välkommen till Ballingo. Vi ska få dig att prata kroatiska.',
    pickLang: 'Visa instruktioner på',
    sv: 'Svenska',
    svSub: 'Hej!',
    en: 'Engelska',
    enSub: 'Hello!',
    note: 'Du kan byta när som helst i Inställningar.',
    cta: 'Kom igång',
  };

  return (
    <div className="app-shell" style={{
      background: T.primary,
      color: '#FFFFFF',
      minHeight: '100vh',
    }}>
      <div style={{
        flex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', textAlign: 'center',
        padding: 'calc(env(safe-area-inset-top) + 24px) 24px 16px',
        gap: 18,
      }}>
        <div style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-0.5px' }}>
          {copy.hello}
        </div>
        <Maja size={160} mood="cheer" tshirt="BOK!" tshirtColor="#FFC857" />
        <div style={{
          fontSize: 18, fontWeight: 700, lineHeight: 1.35, maxWidth: 340,
          opacity: 0.95,
        }}>
          {copy.welcome}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <CroFlag size={28} />
          <span style={{ fontSize: 13, fontWeight: 800, opacity: 0.9 }}>
            HRVATSKI
          </span>
        </div>
      </div>

      <div style={{
        padding: '8px 22px calc(env(safe-area-inset-bottom) + 24px)',
        display: 'flex', flexDirection: 'column', gap: 12,
        background: 'rgba(0,0,0,0.05)',
        borderTopLeftRadius: 24, borderTopRightRadius: 24,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 900, letterSpacing: '0.12em',
          textTransform: 'uppercase', textAlign: 'center', opacity: 0.85,
          marginTop: 4,
        }}>
          {copy.pickLang}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <LangPick value="sv" current={lang} label={copy.sv} sub={copy.svSub} onSelect={setLang} />
          <LangPick value="en" current={lang} label={copy.en} sub={copy.enSub} onSelect={setLang} />
        </div>
        <div style={{
          fontSize: 11, fontWeight: 600, opacity: 0.75, textAlign: 'center', marginTop: 2,
        }}>
          {copy.note}
        </div>
        <ChunkyButton color="gold" onClick={start} style={{ width: '100%', marginTop: 8 }}>
          {copy.cta} →
        </ChunkyButton>
      </div>
    </div>
  );
}
